import MuiGrid from '@mui/material/Grid';
import type { GridProps as MuiGridProps, GridSize } from '@mui/material/Grid';
import { forwardRef } from 'react';

/**
 * Compatibility wrapper for MUI Grid v7
 * Maps the old Grid API (item, xs, sm, md, lg, xl) to new Grid API (size)
 *
 * Old usage: <Grid item xs={12} sm={6}>
 * New usage: <Grid size={{ xs: 12, sm: 6 }}>
 */

type LegacyBreakpoint = number | 'auto' | boolean;

interface LegacyGridProps extends Omit<MuiGridProps, 'size'> {
  item?: boolean;
  xs?: LegacyBreakpoint;
  sm?: LegacyBreakpoint;
  md?: LegacyBreakpoint;
  lg?: LegacyBreakpoint;
  xl?: LegacyBreakpoint;
}

// Convert legacy breakpoint value to new GridSize format
const convertBreakpoint = (value: LegacyBreakpoint | undefined): GridSize | undefined => {
  if (value === undefined) return undefined;
  if (value === true) return 'grow';
  if (value === 'auto') return 'auto';
  if (value === false) return false;
  return value;
};

export const Grid = forwardRef<HTMLDivElement, LegacyGridProps>(
  function Grid({ item, xs, sm, md, lg, xl, ...props }, ref) {
    // Build the size object from legacy breakpoint props
    const hasBreakpoints = xs !== undefined || sm !== undefined || md !== undefined || lg !== undefined || xl !== undefined;

    const size = hasBreakpoints ? {
      ...(xs !== undefined && { xs: convertBreakpoint(xs) }),
      ...(sm !== undefined && { sm: convertBreakpoint(sm) }),
      ...(md !== undefined && { md: convertBreakpoint(md) }),
      ...(lg !== undefined && { lg: convertBreakpoint(lg) }),
      ...(xl !== undefined && { xl: convertBreakpoint(xl) }),
    } : undefined;

    return <MuiGrid ref={ref} size={size} {...props} />;
  }
);

export default Grid;
